import { useCallback, useState, useEffect } from 'react'
import { Checkbox, Select, Text } from '@truepill/react-capsule'
import { AttemptStatuses, AttemptStatusesDisplay, CopayStatus, LaunchDarkly, UserRoles } from '@truepill/tpos-types'
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg'
import AuthLimited from 'components/AuthLimited'
import { CompressedFormColumn, FormItem, FormItems } from 'components/ColumnForm'
import CopayMedicationSearch from 'components/CopayMedicationSearch'
import type { CustomerIdFilterSetting } from 'components/CustomerSearch'
import CustomerSearch from 'components/CustomerSearch'
import EscriptMedicationSearch from 'components/EscriptMedicationSearch'
import EscriptShowRejectedCheckbox from 'components/EscriptShowRejectedCheckbox'
import IntakeIssueStatusSearch from 'components/IntakeIssueStatusSearch'
import OrderMedicationSearch from 'components/OrderMedicationSearch'
import OrderOTCSearch from 'components/OrderOTCSearch'
import PaymentTypeSelect from 'components/PaymentTypeSelect'
import PodSelect from 'components/PodSelect'
import PrescriptionMedicationSearch from 'components/PrescriptionMedicationSearch'
import PrintStatusSelect from 'components/PrintStatusSelect'
import { PriorAuthorizationStatusMultiSelect } from 'components/PriorAuthorization'
import RejectCodeCategorySearch from 'components/RejectCodeCategorySearch'
import RejectCodeSearch from 'components/RejectCodeSearch'
import SaturdayDeliverySelect from 'components/SaturdayDeliverySelect'
import ShelfLocationsSelect from 'components/ShelfLocationsSelect'
import ShippingSelect from 'components/ShippingSelect'
import SortDropdown from 'components/SortDropdown'
import SpecialHandlingSelect from 'components/SpecialHandlingSelect'
import StatesSelect from 'components/StatesSelect'
import StatusSearch from 'components/StatusSearch'
import TriageSearch from 'components/TriageSearch'
import WeatherZonesSelect from 'components/WeatherZonesSelect'
import useFulfillmentQueue, { FulfillmentQueueName } from 'hooks/useFulfillmentQueue'
import useIsOtcOnlyUser from 'hooks/useIsOtcOnlyUser'
import useSetFormDataValue from 'hooks/useSetFormDataValue'
import { FILTER_NAMESPACE } from 'NameSpaces'
import { useFormDataContext } from 'providers/FormDataProvider'
import { useFlag } from 'providers/LaunchDarklyProvider'
import { usePlusClient } from 'providers/VisionRouter'
import styled from 'styled-components'
import { borderColor, capsuleLightGreyColor, primaryBackgroundColor, primaryColor } from 'styles/styleVariables'
import type { Medication, PaymentType, ShippingMethod } from 'types'

const ResetButton = styled.button`
  display: flex;
  padding: 8px 10px;
  font-weight: 600;
  margin-top: 1rem;
  background: ${primaryBackgroundColor};
  border-radius: 4px;
  justify-content: center;
  border: 2px solid ${borderColor};
  width: 100%;
  font-size: 1em;
  :active {
    background: ${primaryColor};
    color: ${primaryBackgroundColor};
  }
  :focus,
  :hover {
    outline-color: ${primaryColor};
    border: 2px solid ${primaryColor};
  }
`

const FiltersHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  h3 {
    margin-left: 0.4em;
  }
`

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 0.5rem;
`

const DisableCheckboxContainer = styled.div<{ disabled?: boolean }>`
  ${({ disabled }) =>
    disabled &&
    `
    label {
      color: ${capsuleLightGreyColor};
    }
  `}
`

const Reset = (): JSX.Element => {
  const { routeToQuery, tokenContext } = usePlusClient()
  const { clearValues } = useFormDataContext()
  return (
    <ResetButton
      onClick={() => {
        clearValues(FILTER_NAMESPACE)
        routeToQuery({ locationId: tokenContext?.locationId })
      }}
    >
      Reset
    </ResetButton>
  )
}

const Header = (): JSX.Element => (
  <FiltersHeading>
    <FilterIcon fill={'black'} />
    <h3>Filters</h3>
  </FiltersHeading>
)

export interface QueuesFilterState {
  medications?: Medication[]
  medication?: string
  customerIds: CustomerIdFilterSetting
  showRobot?: boolean
  showTriage?: boolean
  overdue?: boolean
  batchOrders?: boolean
  reservedForInvestigation?: boolean
  showRejected?: boolean
}

type PrescriptionsFilterProps = {
  isVisible?: boolean
  shippingMethod?: ShippingMethod
}

export const PrescriptionsFilter = ({ isVisible }: PrescriptionsFilterProps): JSX.Element => {
  return (
    <CompressedFormColumn>
      <Header />
      <FormItems>
        <FormItem>
          <p>Medications</p>
          <PrescriptionMedicationSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <CustomerSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <p>Status</p>
          <StatusSearch isVisible={isVisible} />
        </FormItem>
        <Reset />
      </FormItems>
    </CompressedFormColumn>
  )
}

export const EscriptsFilter = ({ isVisible }: PrescriptionsFilterProps): JSX.Element => {
  return (
    <CompressedFormColumn>
      <Header />
      <FormItems>
        <FormItem>
          <p>Medications</p>
          <EscriptMedicationSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <CustomerSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <p>Additional Options</p>
          <EscriptShowRejectedCheckbox />
        </FormItem>
        <Reset />
      </FormItems>
    </CompressedFormColumn>
  )
}

export const IntakeIssuesFilter = ({ isVisible }: PrescriptionsFilterProps): JSX.Element => {
  return (
    <CompressedFormColumn>
      <Header />
      <FormItems>
        <FormItem>
          <CustomerSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <p>Status</p>
          <IntakeIssueStatusSearch isVisible={isVisible} />
        </FormItem>
        <Reset />
      </FormItems>
    </CompressedFormColumn>
  )
}

export const OrdersFilter = ({ isVisible }: PrescriptionsFilterProps): JSX.Element => {
  const { routeToMergedQuery, currentLocation } = usePlusClient()
  const paymentType = currentLocation?.queryMap?.paymentType || undefined
  const isOtcOnlyUser = useIsOtcOnlyUser()

  return (
    <CompressedFormColumn>
      <Header />
      <FormItems>
        {!isOtcOnlyUser && (
          <FormItem>
            <OrderMedicationSearch isVisible={isVisible} />
          </FormItem>
        )}
        <FormItem>
          <CustomerSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <StatesSelect label="Ship-to State" />
        </FormItem>
        <FormItem>
          <p>Status</p>
          <StatusSearch isVisible={isVisible} statusType="order" />
        </FormItem>
        <FormItem>
          <p>Triage Reasons</p>
          <TriageSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <p>Payment Type</p>
          <PaymentTypeSelect
            clearable
            value={paymentType}
            onSelect={(paymentType: PaymentType) => {
              routeToMergedQuery({ paymentType: paymentType.includes('All') ? undefined : paymentType })
            }}
          />
        </FormItem>
        <FormItem>
          <p>Sort:</p>
          <SortDropdown />
        </FormItem>
        <Reset />
      </FormItems>
    </CompressedFormColumn>
  )
}

export const CopaysFilter = ({ isVisible }: PrescriptionsFilterProps): JSX.Element => {
  return (
    <CompressedFormColumn>
      <Header />
      <FormItems>
        <FormItem>
          <CopayMedicationSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <CustomerSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <StatesSelect label="Ship-to State" />
        </FormItem>
        <FormItem>
          <p>Status</p>
          <StatusSearch isVisible={isVisible} statusType="copay" />
        </FormItem>
        <FormItem>
          <p>Triage Reasons</p>
          <TriageSearch isVisible={isVisible} isCopay={true} />
        </FormItem>
        <Reset />
      </FormItems>
    </CompressedFormColumn>
  )
}

export const QueuesFilter = ({ isVisible }: PrescriptionsFilterProps): JSX.Element => {
  const {
    routeToMergedQuery,
    currentLocation,
    QueryToolkit: { StringBoolean },
  } = usePlusClient()
  const { formData } = useFormDataContext()
  const filters = formData[FILTER_NAMESPACE] as unknown as QueuesFilterState
  const setFilterValue = useSetFormDataValue(FILTER_NAMESPACE)
  const fulfillmentQueue = useFulfillmentQueue()
  const isOtcOnlyUser = useIsOtcOnlyUser()
  const enableSaturdayDeliveryFilter =
    useFlag(LaunchDarkly.FeatureFlags.TEMP_ENABLE_SATURDAY_DELIVERY_ELIGIBILITY_FILTER) ?? false
  const showShowRobotFilter =
    !isOtcOnlyUser &&
    fulfillmentQueue &&
    [FulfillmentQueueName.PV2, FulfillmentQueueName.Packing, FulfillmentQueueName.Complete].includes(
      fulfillmentQueue.name,
    )
  const showMultiPetOrdersFilter =
    fulfillmentQueue &&
    ![
      FulfillmentQueueName.Copay,
      FulfillmentQueueName.PriorAuthorization,
      FulfillmentQueueName.PriorAuthorizationNew,
      FulfillmentQueueName.Adjudication,
    ].includes(fulfillmentQueue.name)
  const showRxOtcOnlyOrdersFilters =
    !isOtcOnlyUser &&
    fulfillmentQueue &&
    [
      FulfillmentQueueName.PV1,
      FulfillmentQueueName.Fill,
      FulfillmentQueueName.PV2,
      FulfillmentQueueName.Triage,
    ].includes(fulfillmentQueue.name)
  const showShelfLocationsFilter =
    fulfillmentQueue &&
    (fulfillmentQueue.name === FulfillmentQueueName.Packing ||
      (!isOtcOnlyUser && [FulfillmentQueueName.Fill, FulfillmentQueueName.Triage].includes(fulfillmentQueue.name)))
  const queryMap = currentLocation?.queryMap || {}
  const paymentType = currentLocation?.queryMap?.paymentType || undefined
  const hasPickSlipPrints = StringBoolean(currentLocation?.queryMap?.hasPickSlipPrints)
  const otcOnly = StringBoolean(currentLocation?.queryMap?.otcOnly)
  const rxOtcOnly = StringBoolean(currentLocation?.queryMap?.rxOtcOnly)
  const copayStatus = currentLocation?.queryMap?.copayStatus
  const shippingMethods = currentLocation?.queryMap?.shippingMethods || undefined
  const podNames = currentLocation?.queryMap?.podNames || undefined
  const saturdayDeliveryShippingMethod = currentLocation?.queryMap?.saturdayDeliveryShippingMethod || undefined
  const saturdayDeliveryOptIn = StringBoolean(currentLocation?.queryMap?.saturdayDeliveryOptIn)

  const showSaturdayDeliveryFilter =
    enableSaturdayDeliveryFilter &&
    fulfillmentQueue &&
    [
      FulfillmentQueueName.PV1,
      FulfillmentQueueName.Fill,
      FulfillmentQueueName.PV2,
      FulfillmentQueueName.Packing,
    ].includes(fulfillmentQueue.name)

  const isCopay = fulfillmentQueue && fulfillmentQueue.name === FulfillmentQueueName.Copay
  const isPriorAuthorizationOrder =
    fulfillmentQueue && fulfillmentQueue.name === FulfillmentQueueName.PriorAuthorizationNew
  const isPriorAuthorizationCopay = isCopay && copayStatus === CopayStatus.PriorAuthorization
  const isPriorAuthorization = isPriorAuthorizationOrder || isPriorAuthorizationCopay
  const canSearchByOTCMedication =
    fulfillmentQueue?.name &&
    [FulfillmentQueueName.Packing, FulfillmentQueueName.Triage].includes(fulfillmentQueue?.name)

  const showTriageReasonFilter =
    fulfillmentQueue &&
    (fulfillmentQueue.name === FulfillmentQueueName.Triage || (isCopay && copayStatus === CopayStatus.Triage))
  // duplicated logic see QueuesSection.tsx, getOrderVariables
  const triageChecked = StringBoolean(queryMap.showTriage) || fulfillmentQueue?.name === FulfillmentQueueName.Triage
  return (
    <CompressedFormColumn>
      <Header />
      <FormItems>
        {!isPriorAuthorization && !isOtcOnlyUser && (
          <FormItem>
            {isCopay ? (
              <CopayMedicationSearch isVisible={isVisible} />
            ) : (
              <OrderMedicationSearch isVisible={isVisible} />
            )}
          </FormItem>
        )}
        {canSearchByOTCMedication && (
          <FormItem>
            <OrderOTCSearch isVisible={isVisible} />
          </FormItem>
        )}
        <FormItem>
          <CustomerSearch isVisible={isVisible} />
        </FormItem>
        {isPriorAuthorization && (
          <>
            <FormItem>
              <p>Status</p>
              <PriorAuthorizationStatusMultiSelect />
            </FormItem>
            <FormItem>
              <StyledCheckbox
                label="Manual Prior Authorizations"
                checked={!!StringBoolean(currentLocation.queryMap?.manualPriorAuthorizations)}
                onCheckedChange={checked => {
                  routeToMergedQuery({ manualPriorAuthorizations: checked ? true : undefined, page: 0 })
                }}
              />
            </FormItem>
          </>
        )}
        {fulfillmentQueue && fulfillmentQueue.name === FulfillmentQueueName.Packing && (otcOnly || rxOtcOnly) && (
          <AuthLimited
            roles={[
              UserRoles.Admin,
              UserRoles.Pharmacist,
              UserRoles.LeadPharmacist,
              UserRoles.Technician,
              UserRoles.Warehouse,
              UserRoles.LeadWarehouse,
              UserRoles.OtcOnly,
              UserRoles.LeadOtcOnly,
            ]}
          >
            <FormItem>
              <PrintStatusSelect
                withCapsule
                value={hasPickSlipPrints}
                onSelect={printStatus => {
                  routeToMergedQuery({ hasPickSlipPrints: printStatus === null ? undefined : printStatus })
                }}
              />
            </FormItem>
          </AuthLimited>
        )}
        {showShelfLocationsFilter && (
          <FormItem>
            <ShelfLocationsSelect label="Shelf Locations" isVisible={isVisible} />
          </FormItem>
        )}
        {fulfillmentQueue && !isPriorAuthorization && (
          <FormItem>
            <StatesSelect label="Ship-to State" />
          </FormItem>
        )}
        {fulfillmentQueue &&
          [
            FulfillmentQueueName.PV1,
            FulfillmentQueueName.Triage,
            FulfillmentQueueName.Fill,
            FulfillmentQueueName.PV2,
            FulfillmentQueueName.Packing,
            FulfillmentQueueName.Complete,
            FulfillmentQueueName.Automation,
          ].includes(fulfillmentQueue.name) && (
            <FormItem>
              <WeatherZonesSelect label="Weather Zone" />
            </FormItem>
          )}
        {fulfillmentQueue && !isPriorAuthorization && !isCopay && !isOtcOnlyUser && (
          <FormItem>
            <SpecialHandlingSelect />
          </FormItem>
        )}
        {fulfillmentQueue &&
          [
            FulfillmentQueueName.PV1,
            FulfillmentQueueName.Triage,
            FulfillmentQueueName.Fill,
            FulfillmentQueueName.PV2,
            FulfillmentQueueName.Packing,
            FulfillmentQueueName.Complete,
          ].includes(fulfillmentQueue.name) && (
            <FormItem>
              <PaymentTypeSelect
                withCapsule
                clearable
                multipleFills
                value={paymentType}
                onSelect={(paymentType: PaymentType) => {
                  routeToMergedQuery({
                    paymentType: paymentType.includes('All') ? undefined : paymentType,
                  })
                }}
              />
            </FormItem>
          )}
        {fulfillmentQueue && !isPriorAuthorization && !isCopay && (
          <FormItem>
            <ShippingSelect
              multiSelect
              value={shippingMethods}
              onSelect={(shippingMethods: ShippingMethod[]) => {
                routeToMergedQuery({
                  shippingMethods: shippingMethods.includes('All' as ShippingMethod) ? undefined : shippingMethods,
                })
              }}
            />
          </FormItem>
        )}
        {showSaturdayDeliveryFilter && (
          <FormItem>
            <SaturdayDeliverySelect
              shippingMethod={saturdayDeliveryShippingMethod}
              optIn={saturdayDeliveryOptIn}
              onSelect={({ shippingMethod, optIn }) => {
                routeToMergedQuery({
                  saturdayDeliveryShippingMethod: shippingMethod,
                  saturdayDeliveryOptIn: optIn || undefined,
                  page: 0,
                })
              }}
            />
          </FormItem>
        )}
        {!isOtcOnlyUser && fulfillmentQueue && (
          <FormItem>
            <PodSelect
              value={podNames}
              onSelect={(podNames?: string[]) => {
                // set the podNames, set the batchOrders filter to filter out batch orders if podnames is set
                // (or unset the batch filter if this is removing the pod filters), set page to 0
                routeToMergedQuery({
                  podNames: podNames?.includes('All') ? undefined : podNames,
                  batchOrders: podNames?.length && !podNames.includes('All') ? false : undefined,
                  page: 0,
                })
              }}
              disabled={queryMap?.ignorePodDrugs}
            />
          </FormItem>
        )}
        {fulfillmentQueue && [FulfillmentQueueName.Adjudication].includes(fulfillmentQueue.name) && (
          <FormItem>
            <p>Reject code</p>
            <RejectCodeSearch isVisible={isVisible} />
          </FormItem>
        )}
        {fulfillmentQueue && [FulfillmentQueueName.Adjudication].includes(fulfillmentQueue.name) && (
          <FormItem>
            <p>Reject Category</p>
            <RejectCodeCategorySearch isVisible={isVisible} />
          </FormItem>
        )}
        {showTriageReasonFilter && (
          <FormItem>
            <p>Triage Reasons</p>
            <TriageSearch isVisible={isVisible} isCopay={isCopay} />
          </FormItem>
        )}
        {!isPriorAuthorization && (
          <FormItem>
            <Text as="h4" bold>
              Additional options
            </Text>
            {!isCopay && (
              <StyledCheckbox
                label="Only show near due and overdue fills"
                checked={Boolean(filters.overdue)}
                onCheckedChange={checked => {
                  setFilterValue('overdue', !!checked)
                }}
              />
            )}
            {!isCopay && (
              <StyledCheckbox
                label="Reserved for investigation"
                checked={Boolean(currentLocation.queryMap?.reservedForInvestigation)}
                onCheckedChange={checked => {
                  routeToMergedQuery({ reservedForInvestigation: checked ? true : undefined, page: 0 })
                }}
              />
            )}
            {!isOtcOnlyUser && (
              <DisableCheckboxContainer
                disabled={
                  StringBoolean(currentLocation.queryMap?.batchOrders) === false ||
                  StringBoolean(currentLocation.queryMap?.multiPetOrders)
                }
              >
                <StyledCheckbox
                  label="Only show batch orders"
                  checked={!!StringBoolean(currentLocation.queryMap?.batchOrders)}
                  disabled={
                    StringBoolean(currentLocation.queryMap?.batchOrders) === false ||
                    StringBoolean(currentLocation.queryMap?.multiPetOrders)
                  }
                  onCheckedChange={checked => {
                    routeToMergedQuery({ batchOrders: checked ? true : undefined, page: 0 })
                  }}
                />
              </DisableCheckboxContainer>
            )}
            {fulfillmentQueue && FulfillmentQueueName.Packing === fulfillmentQueue.name && (
              <DisableCheckboxContainer
                disabled={
                  StringBoolean(currentLocation.queryMap?.batchOrders) ||
                  StringBoolean(currentLocation.queryMap?.multiPetOrders)
                }
              >
                <StyledCheckbox
                  label="Show single item OTC orders"
                  checked={!!StringBoolean(currentLocation.queryMap?.otcOneItem)}
                  disabled={StringBoolean(currentLocation.queryMap?.otcOneItem) === false}
                  onCheckedChange={checked => {
                    routeToMergedQuery({ otcOneItem: checked ? true : undefined, otcMultiItems: undefined, page: 0 })
                  }}
                />
                <StyledCheckbox
                  label="Show batch OTC orders"
                  checked={!!StringBoolean(currentLocation.queryMap?.otcMultiItems)}
                  disabled={StringBoolean(currentLocation.queryMap?.otcMultiItems) === false}
                  onCheckedChange={checked => {
                    routeToMergedQuery({ otcMultiItems: checked ? true : undefined, otcOneItem: undefined, page: 0 })
                  }}
                />
              </DisableCheckboxContainer>
            )}

            {showMultiPetOrdersFilter && (
              <DisableCheckboxContainer
                disabled={!!(StringBoolean(currentLocation.queryMap?.batchOrders) !== undefined)}
              >
                <StyledCheckbox
                  label="Only show multi pet orders"
                  checked={!!StringBoolean(currentLocation.queryMap?.multiPetOrders)}
                  disabled={!!(StringBoolean(currentLocation.queryMap?.batchOrders) !== undefined)}
                  onCheckedChange={checked => {
                    routeToMergedQuery({ multiPetOrders: checked ? true : undefined, page: 0 })
                  }}
                />
              </DisableCheckboxContainer>
            )}

            {!isOtcOnlyUser && (
              <DisableCheckboxContainer
                disabled={
                  !!StringBoolean(currentLocation.queryMap?.batchOrders) ||
                  !!StringBoolean(currentLocation.queryMap?.multiPetOrders)
                }
              >
                <StyledCheckbox
                  label="Only show non-batch orders"
                  checked={StringBoolean(currentLocation.queryMap?.batchOrders) === false}
                  disabled={
                    !!StringBoolean(currentLocation.queryMap?.batchOrders) ||
                    !!StringBoolean(currentLocation.queryMap?.multiPetOrders)
                  }
                  onCheckedChange={checked => {
                    routeToMergedQuery({ batchOrders: checked ? false : undefined, page: 0 })
                  }}
                />
              </DisableCheckboxContainer>
            )}
            {!isOtcOnlyUser && (
              <DisableCheckboxContainer
                disabled={
                  !!StringBoolean(currentLocation.queryMap?.batchOrders) ||
                  !!StringBoolean(currentLocation.queryMap?.multiPetOrders) ||
                  currentLocation?.queryMap?.podNames?.length > 0
                }
              >
                <StyledCheckbox
                  label="Only show non-pod orders"
                  checked={StringBoolean(currentLocation.queryMap?.ignorePodDrugs) === true}
                  disabled={
                    !!StringBoolean(currentLocation.queryMap?.batchOrders) ||
                    !!StringBoolean(currentLocation.queryMap?.multiPetOrders) ||
                    currentLocation?.queryMap?.podNames?.length > 0
                  }
                  onCheckedChange={checked => {
                    routeToMergedQuery({
                      ignorePodDrugs: checked ? true : undefined,
                      batchOrders: checked ? false : undefined,
                      page: 0,
                    })
                  }}
                />
              </DisableCheckboxContainer>
            )}

            {showRxOtcOnlyOrdersFilters && (
              <StyledCheckbox
                label="Only show Rx+OTC orders"
                checked={!!StringBoolean(currentLocation.queryMap?.rxOtcOnly)}
                onCheckedChange={checked => {
                  routeToMergedQuery({ rxOtcOnly: checked ? true : undefined, page: 0 })
                }}
              />
            )}

            {showShowRobotFilter && (
              <StyledCheckbox
                label="Show robot"
                checked={Boolean(currentLocation.queryMap?.showRobot)}
                onCheckedChange={checked => {
                  routeToMergedQuery({ showRobot: checked ? true : undefined, page: 0 })
                }}
              />
            )}
            {fulfillmentQueue && ![FulfillmentQueueName.Triage].includes(fulfillmentQueue.name) && !isCopay && (
              <StyledCheckbox
                label="Show triage"
                checked={triageChecked}
                onCheckedChange={checked => {
                  routeToMergedQuery({ showTriage: checked !== undefined ? checked : false, page: 0 })
                }}
              />
            )}
            {fulfillmentQueue &&
              [FulfillmentQueueName.Triage, FulfillmentQueueName.Complete].includes(fulfillmentQueue.name) && (
                <DisableCheckboxContainer disabled={isOtcOnlyUser}>
                  <StyledCheckbox
                    label="Only show OTC orders"
                    checked={StringBoolean(currentLocation.queryMap?.otcOnly)}
                    disabled={isOtcOnlyUser}
                    onCheckedChange={checked => {
                      routeToMergedQuery({ otcOnly: checked ? true : undefined, page: 0 })
                    }}
                  />
                </DisableCheckboxContainer>
              )}
          </FormItem>
        )}
        <Reset />
      </FormItems>
    </CompressedFormColumn>
  )
}

export const ExternalTransfersFilter = ({ isVisible }: PrescriptionsFilterProps): JSX.Element => {
  const {
    currentLocation: { queryMap },
    routeToMergedQuery,
    QueryToolkit: { StringBoolean },
  } = usePlusClient()
  const filterRequiresPV1 = StringBoolean(queryMap.filterRequiresPV1) === true
  const filterPending = StringBoolean(queryMap.filterPending) === true
  const filterAutomationFailed = StringBoolean(queryMap.filterAutomationFailed) === true
  const [attemptStatus, setAttemptStatus] = useState<{ label: string; value: string } | null>(null)

  const attempStatusOptions = [
    { label: AttemptStatusesDisplay[AttemptStatuses.WrongNumber], value: AttemptStatuses.WrongNumber },
    {
      label: AttemptStatusesDisplay[AttemptStatuses.CallbackRequested],
      value: AttemptStatuses.CallbackRequested,
    },
    {
      label: AttemptStatusesDisplay[AttemptStatuses.PharmacyClosed],
      value: AttemptStatuses.PharmacyClosed,
    },
    { label: AttemptStatusesDisplay[AttemptStatuses.MaxHold], value: AttemptStatuses.MaxHold },
    { label: AttemptStatusesDisplay[AttemptStatuses.NoAnswer], value: AttemptStatuses.NoAnswer },
  ]

  useEffect(() => {
    if (queryMap.attemptStatus) {
      const statusOption = attempStatusOptions.find(option => option.value === queryMap.attemptStatus)

      if (statusOption) {
        setAttemptStatus(statusOption)
      }
    } else {
      setAttemptStatus(null)
    }
  }, [queryMap.attemptStatus])

  const handleSelectAttemptStatus = useCallback(
    (selectedAttemptStatus: { label: string; value: string } | null | undefined) => {
      if (selectedAttemptStatus) {
        routeToMergedQuery({
          attemptStatus: selectedAttemptStatus.value,
          page: 0,
        })
        setAttemptStatus(selectedAttemptStatus)
      } else {
        routeToMergedQuery({
          attemptStatus: undefined,
          page: 0,
        })
        setAttemptStatus(null)
      }
    },
    [routeToMergedQuery],
  )

  return (
    <CompressedFormColumn>
      <FormItems>
        <FormItem>
          <CustomerSearch isVisible={isVisible} />
        </FormItem>
        <FormItem>
          <Text style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.8rem' }}>Transfer status</Text>
          <StyledCheckbox
            label="Prescription Verification"
            checked={filterRequiresPV1}
            onCheckedChange={checked =>
              routeToMergedQuery({
                filterRequiresPV1: checked ? true : undefined,
                page: 0,
              })
            }
          />
          <StyledCheckbox
            label="Ready To Transfer"
            checked={filterPending}
            onCheckedChange={checked =>
              routeToMergedQuery({
                filterPending: checked ? true : undefined,
                page: 0,
              })
            }
          />
          <StyledCheckbox
            label="Fax Failed"
            checked={filterAutomationFailed}
            onCheckedChange={checked =>
              routeToMergedQuery({
                filterAutomationFailed: checked ? true : undefined,
                page: 0,
              })
            }
          />
          <Select
            css={{ marginTop: '0.8rem' }}
            label="Attempt status"
            value={attemptStatus}
            onChange={handleSelectAttemptStatus}
            options={attempStatusOptions}
            selectedKey="label"
            placeholder="Select one"
          />
        </FormItem>
        <Reset />
      </FormItems>
    </CompressedFormColumn>
  )
}
